import React from "react";
import { motion } from "framer-motion";
import { Star, Bookmark, Sparkle, FloatingBook, SpeechBubble, ScribbleArrow } from "@/components/site/Scribbles";

const EASE = [0.22, 1, 0.36, 1];
const pop = { hidden: { opacity: 0, scale: 0.7 }, show: (i) => ({ opacity: 1, scale: 1, transition: { duration: 0.6, ease: EASE, delay: 0.5 + i * 0.1 } }) };

const FloatChip = ({ children, className, i, drift = 12, dur = 5 }) => (
  <motion.div variants={pop} custom={i} initial="hidden" animate="show" className={className}>
    <motion.div animate={{ y: [0, -drift, 0] }} transition={{ duration: dur + i, repeat: Infinity, ease: "easeInOut" }}>
      {children}
    </motion.div>
  </motion.div>
);

// Rich, playful doodle cloud around the mascot — desktop left panel only.
export function AuthDecor({ className = "" }) {
  return (
    <div className={className} aria-hidden="true">
      <FloatChip i={0} className="absolute -left-8 top-4 rotate-[-6deg]">
        <div className="rounded-2xl rounded-bl-md border border-line bg-card px-3.5 py-2 shadow-soft">
          <p className="text-xs font-medium text-ink">"Just one more chapter…"</p>
        </div>
      </FloatChip>
      <FloatChip i={1} className="absolute -right-6 top-16 rotate-[5deg]">
        <span className="rounded-full bg-sage/70 px-3 py-1.5 text-xs font-semibold text-[#37432f] shadow-soft dark:bg-sage/25 dark:text-sage">Cozy reads</span>
      </FloatChip>
      <FloatChip i={2} className="absolute -left-10 bottom-28">
        <div className="text-yellow"><SpeechBubble className="h-10 w-14" /></div>
      </FloatChip>
      <div className="absolute -right-2 top-40 text-coral"><Star className="h-5 w-5 animate-floatSlow" /></div>
      <motion.div className="absolute right-6 bottom-10 text-blue/70" animate={{ rotate: [0, 8, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}>
        <Bookmark className="h-8 w-8" />
      </motion.div>
      <motion.div className="absolute -left-6 top-1/2 text-coral" animate={{ y: [0, -14, 0], rotate: [-4, 3, -4] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
        <FloatingBook className="h-10 w-12" />
      </motion.div>
      <motion.div className="absolute -right-8 -bottom-4 text-yellow" animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
        <Sparkle className="h-5 w-5" />
      </motion.div>
      <div className="absolute -bottom-8 left-1/2 hidden -translate-x-1/2 rotate-2 lg:block">
        <span className="font-hand text-2xl text-blue">scribbled a note…</span>
        <ScribbleArrow className="ml-10 h-8 w-12 text-coral" />
      </div>
    </div>
  );
}

// Very low-key full-bleed backdrop used behind the form (mobile, and as a
// soft ambient layer on desktop) — blurred colour blobs + faint doodles.
export function AuthBackdrop({ className = "" }) {
  return (
    <div className={className} aria-hidden="true">
      <div
        className="absolute -left-24 -top-24 h-72 w-72 rounded-full opacity-70"
        style={{ background: "radial-gradient(circle at 40% 40%, rgba(197,215,189,0.55), rgba(197,215,189,0) 70%)" }}
      />
      <div
        className="absolute -right-16 top-1/3 h-80 w-80 rounded-full opacity-60"
        style={{ background: "radial-gradient(circle at 60% 40%, rgba(240,199,127,0.45), rgba(240,199,127,0) 70%)" }}
      />
      <div
        className="absolute -bottom-24 left-1/4 h-72 w-72 rounded-full opacity-60"
        style={{ background: "radial-gradient(circle at 50% 50%, rgba(204,111,105,0.28), rgba(204,111,105,0) 70%)" }}
      />
      <div className="absolute left-[8%] top-[12%] text-coral/25"><Star className="h-6 w-6" /></div>
      <div className="absolute right-[12%] top-[22%] text-blue/25"><Bookmark className="h-7 w-7" /></div>
      <div className="absolute bottom-[18%] right-[8%] text-yellow/30"><Sparkle className="h-6 w-6" /></div>
      <div className="absolute bottom-[10%] left-[10%] text-sage/40"><FloatingBook className="h-9 w-11" /></div>
    </div>
  );
}
